// Инициативы: оценка, голосование, разрешение.
// Вынесено из api.ts (Фаза 1, разбор клиента по доменам).

import { request, LONG_ACTION_TIMEOUT_MS } from "./client";
import { type CycleParticipant } from "./common";
import { type CampaignCycleOverview } from "./cycle";
import { type CampaignAvatar, type MediaAsset } from "./media";

export type InitiativeVoteStance = "SUPPORT" | "OPPOSE" | "ABSTAIN";
export type InitiativeRiskLevel = "LOW" | "MEDIUM" | "HIGH";
export type InitiativeInstitutionRole = "PRIMARY" | "SUPPORTING";
export type InitiativeFactionStance = "SUPPORTIVE" | "RESISTANT" | "AFFECTED";
export type ResolutionOutcome =
  "SUCCESS" | "PARTIAL_SUCCESS" | "FAILURE" | "CRITICAL_FAILURE";
export type InitiativeFundingStatus =
  "PENDING" | "FUNDED" | "NOT_FUNDED" | "NOT_APPLICABLE";
export type InitiativeStatus =
  | "DRAFT"
  | "DISCUSSION"
  | "VOTING"
  | "ACCEPTED"
  | "REJECTED"
  | "NOT_FUNDED"
  | "RESOLVED"
  | "MERGED";
export interface InitiativeResourceAssessment {
  code: string;
  displayName: string;
  cost: number;
  available: number;
  reason: string;
}
export interface InitiativeInstitutionAssessment {
  code: string;
  displayName: string;
  role: InitiativeInstitutionRole;
  level: number;
  load: number;
  reason: string;
}
export type InitiativeExpectedEffectKind =
  | "RESOURCE_GAIN"
  | "RESOURCE_PRESERVE"
  | "INDICATOR_GAIN"
  | "THREAT_REDUCE"
  | "INSTITUTION_RELIEF"
  | "FACTION_RELATION"
  | "KNOWLEDGE_GAIN"
  | "NO_DIRECT_RESOURCE";
export type InitiativeAssessmentConfidence = "LOW" | "MEDIUM" | "HIGH";
export interface InitiativeFactionAssessment {
  code: string;
  displayName: string;
  stance: InitiativeFactionStance;
  attitude: number;
  reason: string;
}
export interface InitiativeExpectedEffect {
  kind: InitiativeExpectedEffectKind;
  code: string;
  displayName: string;
  confidence: InitiativeAssessmentConfidence;
  reason: string;
}
export interface InitiativeAssessment {
  resources: InitiativeResourceAssessment[];
  institutions: InitiativeInstitutionAssessment[];
  factions: InitiativeFactionAssessment[];
  expectedEffects: InitiativeExpectedEffect[];
  riskLevel: InitiativeRiskLevel;
  summary: string;
  reasons: string[];
  provider: string;
  version: number;
  locked: boolean;
  assessedAt?: string;
  lockedAt?: string;
}
export interface InitiativeSource {
  id: string;
  title: string;
  authorUserId: string;
  authorDisplayName: string;
}
export interface Initiative {
  id: string;
  authorUserId: string;
  authorCampaignMemberId: string;
  authorDisplayName: string;
  authorAvatar: CampaignAvatar;
  title: string;
  description: string;
  intendedOutcome: string;
  resourceCode: string;
  resourceCost: number;
  institutionCode: string;
  factionCode?: string;
  riskLevel: InitiativeRiskLevel;
  assessment: InitiativeAssessment;
  status: InitiativeStatus;
  linkedActionIds: string[];
  linkedAgreementIds: string[];
  supportCount: number;
  opposeCount: number;
  abstainCount: number;
  myVote?: InitiativeVoteStance;
  isMine: boolean;
  coauthors: CycleParticipant[];
  sourceInitiatives: InitiativeSource[];
  consolidatedIntoId?: string;
  fundingStatus: InitiativeFundingStatus;
  fundingExplanation: string;
  budgetPriority: number;
  images: MediaAsset[];
  createdAt: string;
  updatedAt: string;
}
export interface InitiativeResolution {
  initiativeId: string;
  outcome: ResolutionOutcome;
  score: number;
  rollValue: number;
  explanation: string;
  resourceDeltas: Record<string, number>;
  indicatorDeltas: Record<string, number>;
  institutionDeltas: Record<string, number>;
  factionDeltas: Record<string, number>;
}

export const initiativeApi = {
  saveInitiative: (
    campaignId: string,
    payload: {
      title: string;
      description: string;
      linkedActionIds: string[];
      linkedAgreementIds: string[];
    },
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/initiative`,
      { method: "PUT", body: JSON.stringify(payload) },
      LONG_ACTION_TIMEOUT_MS,
    ),
  withdrawInitiative: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/initiative`,
      { method: "DELETE" },
    ),
  skipInitiative: (campaignId: string) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/initiative/skip`,
      { method: "POST" },
    ),
  castInitiativeVote: (
    campaignId: string,
    initiativeId: string,
    stance: InitiativeVoteStance,
  ) =>
    request<CampaignCycleOverview>(
      `/api/campaigns/${campaignId}/cycles/current/votes`,
      { method: "POST", body: JSON.stringify({ initiativeId, stance }) },
    ),
};
